import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Table, Typography } from 'antd';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { createSearchParams, Link, Outlet, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../../api/useInfiniteAPIQuery';
import Filter from '../../../components/Filter';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import useTypedNavigate from '../../../hooks/useTypedNavigate';
import useTypedParams from '../../../hooks/useTypedParams';
import { withRouteChange } from '../../../services/APM';
import { KeyPairCollection } from '../../../types';

const MindsightKeyPairs = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const keyPairs = useAPIQuery(
    [
      '/mindsight/key-pairs/',
      {
        params: {
          page: parseInt(searchParams.get('page') || '1', 10),
          page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        },
      },
    ],
    {
      keepPreviousData: true,
    }
  );

  const staffUsersPages = useInfiniteAPIQuery(['/staff-users/']);

  const staffUsers = usePrefetchPages(staffUsersPages, (data) => data.results);

  const queryClient = useQueryClient();

  const onCreate = useAPIMutation((_, api) => api.post('/mindsight/key-pairs/'), {
    onSuccess: (keyPair) => {
      navigate({
        pathname: '/:tenant/settings/cs-staff/mindmatch-integration/:key_pair_id/',
        params: { tenant, key_pair_id: keyPair.data.id },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries('/mindsight/key-pairs/');
    },
  });

  return (
    <>
      <Helmet>
        <title>
          {t('Integração Mindmatch | Área do CS')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Outlet />
      <Filter>
        <Col flex="auto" />
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => onCreate.mutate({})}
            disabled={onCreate.isLoading}
            loading={onCreate.isLoading}
          >
            {' '}
            <Trans>Nova chave</Trans>
          </Button>
        </Col>
      </Filter>
      <Table
        rowKey="id"
        dataSource={keyPairs.data?.results}
        loading={keyPairs.isFetching}
        pagination={{
          showTotal: (total) => {
            return t(`Total: {{total}} chaves`, { total });
          },
          total: keyPairs.data?.count,
          onChange: (page, pageSize) => {
            searchParams.set('page', page.toString());
            searchParams.set('pageSize', pageSize.toString());
            setSearchParams(searchParams);
          },
          current: parseInt(searchParams.get('page') || '1', 10),
          pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        }}
      >
        <Table.Column
          title="#"
          render={(record: KeyPairCollection) => (
            <Link
              to={{
                pathname: `${record.id}`,
                search: createSearchParams(searchParams).toString(),
              }}
            >
              #{record.id}
            </Link>
          )}
        />
        <Table.Column
          dataIndex="client_id"
          title={t('Client ID')}
          render={(value) =>
            value ? (
              <Typography.Text code>{value}</Typography.Text>
            ) : (
              <Typography.Text italic>N/D</Typography.Text>
            )
          }
        />
        <Table.Column
          dataIndex="user"
          title={t('Usuário')}
          render={(value) => staffUsers.find((user) => user.id === value)?.username ?? '...'}
        />
        <Table.Column
          dataIndex="created_at"
          title={t('Criado em')}
          render={(value) => moment(value).format('LLL')}
        />
        <Table.Column
          dataIndex="updated_at"
          title={t('Atualizado em')}
          render={(value) => moment(value).format('LLL')}
        />
      </Table>
    </>
  );
};

export default withRouteChange()(MindsightKeyPairs);
